import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Background, Controls, ReactFlow, type Node } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { CopyButton } from '../components/CopyButton';
import { Pager, usePaged } from '../components/Pager';
import { SortHeader } from '../components/SortHeader';
import { OwnerCell, ownerOf, type Owner } from '../components/Ownership';
import { compareBy, useUrlNumber, useUrlSort, useUrlState } from '../lib/useUrlState';
import { Icon } from '../components/Icon';
import { RefreshButton } from '../components/RefreshButton';

const CLUSTERS = gql`
  query Clusters {
    clusters {
      id
      name
      owners {
        ownerId
        role
        displayName
        email
      }
      cloudProvider
      doAccount
      region
      k8sVersion
      lbIp
      nodeCount
      ingressType
      certMode
      alloyOnboarded
      reachability
      namespaceCount
      confidence
      lastSeenAt
      health {
        monitored
        scrapeUpRatio
        nodesReady
        nodesTotal
        podsCrashlooping
        cpuUsedPct
        memUsedPct
        certExpiryDays
      }
    }
  }
`;

interface Health {
  monitored: boolean;
  scrapeUpRatio?: number | null;
  nodesReady?: number | null;
  nodesTotal?: number | null;
  podsCrashlooping?: number | null;
  cpuUsedPct?: number | null;
  memUsedPct?: number | null;
  certExpiryDays?: number | null;
}
interface Cluster {
  id: string;
  name: string;
  owners?: Owner[];
  cloudProvider?: string;
  doAccount?: string;
  region?: string;
  k8sVersion?: string;
  lbIp?: string;
  nodeCount?: number;
  ingressType?: string;
  certMode?: string;
  alloyOnboarded?: boolean;
  reachability?: string;
  namespaceCount: number;
  confidence: string;
  lastSeenAt: string;
  health?: Health;
}
interface Data {
  clusters: Cluster[];
}

/** Derive a health level from the live metrics for the dot color. */
export function healthLevel(h?: Health): 'none' | 'ok' | 'warn' | 'down' {
  if (!h || !h.monitored) return 'none';
  if (h.scrapeUpRatio != null && h.scrapeUpRatio === 0) return 'down';
  const nodesDown = h.nodesReady != null && h.nodesTotal != null && h.nodesReady < h.nodesTotal;
  const scrapeDegraded = h.scrapeUpRatio != null && h.scrapeUpRatio < 1;
  const crashing = (h.podsCrashlooping ?? 0) > 0;
  return nodesDown || scrapeDegraded || crashing ? 'warn' : 'ok';
}
const HEALTH_LABEL: Record<string, string> = { none: 'not monitored', ok: 'healthy', warn: 'degraded', down: 'down' };

export function HealthDot({ h }: { h?: Health }) {
  const lvl = healthLevel(h);
  return (
    <span className="dot-cell" title={HEALTH_LABEL[lvl]}>
      <span className={`health-dot ${lvl}`} />
      <span className={lvl === 'none' ? 'muted' : ''}>{HEALTH_LABEL[lvl]}</span>
    </span>
  );
}

/**
 * Graph node accents by DO account — non-status, non-brand hues.
 * Table account pills stay muted (.badge). Never reuse status green or brand blue.
 */
const ACCENT: Record<string, string> = {
  optisigns: '#a78bfa',
  'optisigns-devqa': '#67e8f9',
  'optisigns-lite': '#f0abfc',
  alphasphere: '#c4b5fd',
  optisound: '#fda4af',
};

// Column registry. `basic` = shown by default (the AWS-style essentials); the
// rest are opt-in via the Columns picker. Order here = column order.
interface Col {
  key: string;
  label: string;
  basic: boolean;
  render: (c: Cluster) => React.ReactNode;
}
const COLUMNS: Col[] = [
  {
    key: 'name',
    label: 'Cluster',
    basic: true,
    render: (c) => (
      <Link className="table-title" to={`/clusters/${encodeURIComponent(c.name)}`}>
        {c.name}
      </Link>
    ),
  },
  { key: 'cloudProvider', label: 'Cloud Provider', basic: true, render: (c) => c.cloudProvider ?? '—' },
  { key: 'nodeCount', label: 'Nodes', basic: true, render: (c) => c.nodeCount ?? '—' },
  {
    key: 'lbIp',
    label: 'LB IP',
    basic: true,
    render: (c) =>
      c.lbIp ? (
        <span className="ip-cell">
          {c.lbIp}
          <CopyButton value={c.lbIp} />
        </span>
      ) : (
        '—'
      ),
  },
  {
    key: 'reachability',
    label: 'Status',
    basic: true,
    render: (c) => (
      <span className={`badge ${c.reachability === 'reachable' ? 'confirmed' : 'inferred'}`}>
        {c.reachability === 'reachable' ? 'reachable' : 'unreachable'}
      </span>
    ),
  },
  { key: 'health', label: 'Health', basic: true, render: (c) => <HealthDot h={c.health} /> },
  // Basic, not opt-in: an unowned production cluster is the finding, and a column
  // you have to switch on is a column nobody sees.
  { key: 'owner', label: 'Owner', basic: true, render: (c) => <OwnerCell owners={c.owners} /> },
  {
    key: 'doAccount',
    label: 'Account',
    basic: false,
    render: (c) => <span className="badge">{c.doAccount ?? '—'}</span>,
  },
  { key: 'region', label: 'Region', basic: false, render: (c) => c.region ?? '—' },
  {
    key: 'k8sVersion',
    label: 'Version',
    basic: false,
    render: (c) => (c.k8sVersion ? <span className="id-compact">{c.k8sVersion}</span> : '—'),
  },
  // Live-health detail columns (opt-in) — null/"—" until Grafana is configured.
  {
    key: 'nodesHealth',
    label: 'Nodes ready',
    basic: false,
    render: (c) =>
      c.health?.nodesTotal != null ? `${c.health.nodesReady ?? 0}/${c.health.nodesTotal}` : '—',
  },
  {
    key: 'crashloops',
    label: 'Crashloops',
    basic: false,
    render: (c) => (c.health?.monitored ? (c.health.podsCrashlooping ?? 0) : '—'),
  },
  { key: 'cpu', label: 'CPU %', basic: false, render: (c) => pct(c.health?.cpuUsedPct) },
  { key: 'mem', label: 'Mem %', basic: false, render: (c) => pct(c.health?.memUsedPct) },
  {
    key: 'certDays',
    label: 'Cert days',
    basic: false,
    render: (c) => (c.health?.certExpiryDays != null ? Math.round(c.health.certExpiryDays) : '—'),
  },
  // Ingress / Namespaces / Alloy / Cert-mode live on the cluster detail page only.
];

function pct(v?: number | null): string {
  return v == null ? '—' : `${v}%`;
}

const LS_KEY = 'infras.clusterColumns';
function loadColumns(): Set<string> {
  try {
    const saved = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (Array.isArray(saved) && saved.length) return new Set(saved);
  } catch {
    /* fall through to defaults */
  }
  return new Set(COLUMNS.filter((c) => c.basic).map((c) => c.key));
}

/** Sortable columns → the scalar behind the rendered cell. Health sub-metrics
 *  sort on their numeric value; a cluster with no health data sorts last. */
const SORT_PICK: Record<string, (c: Cluster) => unknown> = {
  name: (c) => c.name,
  cloudProvider: (c) => c.cloudProvider,
  nodeCount: (c) => c.nodeCount,
  lbIp: (c) => c.lbIp,
  reachability: (c) => c.reachability,
  // Sorts on the owner's label; unowned rows land last either direction, which
  // is what compareBy does with nullish — so "sort by owner" is also "show me
  // the gaps", from the bottom.
  owner: (c) => ownerOf(c.owners)?.displayName || ownerOf(c.owners)?.email,
  doAccount: (c) => c.doAccount,
  region: (c) => c.region,
  crashloops: (c) => c.health?.podsCrashlooping,
  cpu: (c) => c.health?.cpuUsedPct,
  mem: (c) => c.health?.memUsedPct,
  certDays: (c) => c.health?.certExpiryDays,
};

export function ClustersPage() {
  const { data, loading, error, refetch } = useQuery<Data>(CLUSTERS, {
    fetchPolicy: 'cache-and-network',
  });
  // view / filter / sort / page in the URL so a narrowed cluster list is a link
  const [view, setView] = useUrlState('view', 'table');
  const [q, setQ] = useUrlState('q');
  const [accountFilter, setAccountFilter] = useUrlState('account');
  const sort = useUrlSort('name');
  const [page, setPage] = useUrlNumber('page', 1);
  const [visible, setVisible] = useState<Set<string>>(loadColumns);
  const [pickerOpen, setPickerOpen] = useState(false);

  const clusters = data?.clusters ?? [];
  const accounts = useMemo(
    () => [...new Set(clusters.map((c) => c.doAccount || '(unknown)'))].sort(),
    [clusters],
  );
  const reachable = clusters.filter((c) => c.reachability === 'reachable').length;
  const totalNs = clusters.reduce((s, c) => s + (c.namespaceCount || 0), 0);
  const unowned = clusters.filter((c) => !ownerOf(c.owners)).length;
  const cols = COLUMNS.filter((c) => visible.has(c.key));

  const toggleCol = (key: string) => {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      // name is mandatory (it's the link to detail)
      next.add('name');
      localStorage.setItem(LS_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const nodes: Node[] = useMemo(() => buildGraph(clusters), [clusters]);
  const needle = q.trim().toLowerCase();
  const filtered = clusters.filter(
    (c) =>
      (!needle || c.name.toLowerCase().includes(needle)) &&
      (!accountFilter || (c.doAccount || '(unknown)') === accountFilter),
  );
  const sorted = compareBy(filtered, (c) => SORT_PICK[sort.key]?.(c), sort.dir);
  const pg = usePaged(sorted, 25, { page, onPage: setPage });
  const anyFilter = Boolean(needle || accountFilter);
  const clearFilters = () => {
    setQ('');
    setAccountFilter('');
  };

  if (loading && !data)
    return (
      <div className="page">
        <div className="skeleton-row-group">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="skeleton-row" key={i} />
          ))}
        </div>
      </div>
    );
  // Old copy told the reader to run a pnpm command — useless to anyone who isn't
  // holding the repo, and it isn't why the page failed anyway.
  if (error)
    return (
      <div className="page">
        <p className="error">Couldn&apos;t load the clusters — the API didn&apos;t respond.</p>
        <p className="muted">
          The k8s scan keeps running in the background, so nothing is lost. If this persists, ask in
          #devops-infra.
        </p>
      </div>
    );

  return (
    <div className="page">
      <div className="stat-row">
        <Stat label="Clusters" value={clusters.length} />
        <Stat label="Reachable" value={`${reachable} / ${clusters.length}`} />
        <Stat label="DO accounts" value={accounts.length} />
        <Stat label="Namespaces" value={totalNs} />
      </div>

      {/* Phase 6 §3, same as /repos — the count is what makes anyone fix it, and
          it sits next to the table that fixes it. Sorting by Owner puts the
          unowned rows at the bottom in either direction (compareBy keeps nullish
          last), so "show them" is a sort, not another filter to remember. */}
      {unowned > 0 && (
        <p className="unowned-note">
          <strong>{unowned}</strong> of {clusters.length} clusters have no owner.{' '}
          <button className="mini-btn" onClick={() => sort.toggle('owner')}>
            Sort by owner
          </button>{' '}
          — unowned rows land at the bottom. An owner is who an incident here goes to.
        </p>
      )}

      <div className="toolbar">
        <div className="view-toggle">
          <button className={view === 'table' ? 'active' : ''} onClick={() => setView('table')}>
            Table
          </button>
          <button className={view === 'graph' ? 'active' : ''} onClick={() => setView('graph')}>
            Graph
          </button>
        </div>
        {view === 'table' && (
          <div className="filter-row" style={{ flex: 1 }}>
            <RefreshButton refetch={() => refetch().catch(() => undefined)} />
            <input
              placeholder="Search cluster name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select
              value={accountFilter}
              onChange={(e) => setAccountFilter(e.target.value)}
              title="filter by DO account"
            >
              <option value="">All accounts</option>
              {accounts.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
            <span className="filter-count">
              {filtered.length} of {clusters.length}
            </span>
          </div>
        )}
        {view === 'table' && (
          <div className="col-picker">
            <button onClick={() => setPickerOpen((o) => !o)}><Icon name="settings" /> Columns ({cols.length})</button>
            {pickerOpen && (
              <div className="col-picker-panel">
                {COLUMNS.map((col) => (
                  <label key={col.key} className={col.key === 'name' ? 'disabled' : ''}>
                    <input
                      type="checkbox"
                      checked={visible.has(col.key)}
                      disabled={col.key === 'name'}
                      onChange={() => toggleCol(col.key)}
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {clusters.length === 0 ? (
        <p className="muted">
          No clusters yet — they appear once the <code>k8s</code> scan has run.
        </p>
      ) : filtered.length === 0 ? (
        <p className="muted">
          No cluster matches these filters.{' '}
          {anyFilter && (
            <button className="mini-btn" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </p>
      ) : view === 'table' ? (
        <>
          <table className="data-list--primary">
            <thead>
              <tr>
                {cols.map((c) => (
                  SORT_PICK[c.key] ? (
                    <SortHeader key={c.key} label={c.label} sortKey={c.key} sort={sort} />
                  ) : (
                    <th key={c.key}>{c.label}</th>
                  )
                ))}
              </tr>
            </thead>
            <tbody>
              {pg.pageItems.map((cluster) => (
                <tr key={cluster.id} style={{ opacity: cluster.reachability === 'reachable' ? 1 : 0.55 }}>
                  {cols.map((c) => (
                    <td key={c.key}>{c.render(cluster)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={pg.page} pageCount={pg.pageCount} total={pg.total} start={pg.start} pageSize={pg.pageSize} onPage={pg.setPage} />
        </>
      ) : (
        <div className="graph-wrap" style={{ height: 'calc(100vh - 200px)' }}>
          <ReactFlow nodes={nodes} edges={[]} fitView minZoom={0.1}>
            <Background />
            <Controls />
          </ReactFlow>
        </div>
      )}
    </div>
  );
}

function buildGraph(clusters: Cluster[]): Node[] {
  const byAccount = new Map<string, Cluster[]>();
  for (const c of clusters) {
    const a = c.doAccount || '(unknown)';
    (byAccount.get(a) ?? byAccount.set(a, []).get(a)!).push(c);
  }
  const out: Node[] = [];
  let col = 0;
  for (const [account, list] of [...byAccount.entries()].sort()) {
    out.push({
      id: `acct:${account}`,
      position: { x: col * 280, y: 0 },
      data: { label: `${account}\n${list.length} clusters` },
      className: 'text-xs',
      style: {
        background: 'transparent',
        color: ACCENT[account] ?? '#8b93a3',
        border: `1px solid ${ACCENT[account] ?? '#8b93a3'}`,
        borderRadius: 8,
        fontWeight: 600,
        width: 240,
        whiteSpace: 'pre-line',
        textAlign: 'center' as const,
      },
    });
    list
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((c, i) => {
        out.push({
          id: c.id,
          position: { x: col * 280, y: 90 + i * 72 },
          data: {
            label: `${c.name.replace(/^k8s-optisigns-/, '').replace(/^k8s-/, '')}\n${
              c.region ?? '—'
            } · ${c.namespaceCount} ns`,
          },
          className: 'text-2xs',
          style: {
            background: '#181b22',
            color: '#e6e9ef',
            border: `2px solid ${
              c.reachability === 'reachable' ? ACCENT[account] ?? '#8b93a3' : '#e0a83a'
            }`,
            opacity: c.reachability === 'reachable' ? 1 : 0.6,
            borderRadius: 8,
            width: 240,
            whiteSpace: 'pre-line',
            textAlign: 'center' as const,
          },
        });
      });
    col += 1;
  }
  return out;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
    </div>
  );
}
